import React from "react";
import Stack from "@mui/material/Stack";
import {useMediaQuery} from "react-responsive";
import classnames from "classnames";
import {mediaQueryForMobile} from "../HomePage/HomePage";
import Typography from "@mui/material/Typography";

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import {useDealContext} from "../../contexts/DealContext";
import {PopoverInput} from "../../components/PopoverInput";
import EditLocationAltOutlinedIcon from '@mui/icons-material/EditLocationAltOutlined';
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined';
import Tooltip from "@mui/material/Tooltip";
import {LocationModal} from "./LocationModal";
import {useUserContext} from "../../contexts/UserContext";
import {useModalsStore} from "../../stores/modalsStore";
import {useShallow} from "zustand/react/shallow";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import _ from "lodash";
import {buildLocationString} from "../../utils/utils";
import {useTaxRegionFlow} from "../../hooks/useTaxRegionFlow";

export const DealPageInfo = () => {
    const isTabletOrMobile = useMediaQuery({ query: mediaQueryForMobile })
    const { updateDealResponse, dealState, setDealStateWithoutQuoteUpdate, setDealStateWithUpdate, dealColorLight, openListingsModal, isHistorical } = useDealContext()
    const { isFeatureFlagEnabled } = useUserContext()
    const { resolveTaxRegion } = useTaxRegionFlow()

    const {
        openModal,
        closeModalById,
    } = useModalsStore(
        useShallow((state) => ({
            openModal: state.openModal,
            closeModalById: state.closeModalById,
        }))
    )

    // `newDealerLocation`: the modal can stage a different Dealer pick in the same call.
    const saveDealLocation = async (location, shouldSetCustomerLocation, shouldSetDealerLocation, newDealerLocation = location) => {
        closeModalById("LocationModal")
        const formatted_quote = structuredClone(dealState).formatted_quote
        if (location.state !== dealState.state) {
            formatted_quote.fees = []
            formatted_quote.rebates = []
        }

        let updateObj = {}

        if (shouldSetCustomerLocation) {
            updateObj = {
                address_line_one: location.address_line_one,
                address_line_two: location.address_line_two,
                city: location.city,
                state: location.state,
                zipcode: location.zipcode,
                county: location.county,
                formatted_quote: formatted_quote
            }
            // A manual tax rate belongs to the old ZIP (MarketScan taxes by CustomerZIP).
            if (location.zipcode !== dealState.zipcode) updateObj.sales_tax_rate_override = ''
        }
        if (shouldSetDealerLocation) {
            updateObj = {
                ...updateObj,
                dealer_location: newDealerLocation,
                dealer_location_id: newDealerLocation.id,
                dealer_address_line_one: newDealerLocation.address_line_one,
                dealer_address_line_two: newDealerLocation.address_line_two,
                dealer_city: newDealerLocation.city,
                dealer_state: newDealerLocation.state,
                dealer_zipcode: newDealerLocation.zipcode,
                dealer_county: newDealerLocation.county
            }
        }
        if (_.isEmpty(updateObj)) return

        // Only the customer ZIP determines tax, so a dealer-only change never
        // prompts. The lookup runs BEFORE anything is sent, so a multi-region
        // ZIP never produces a quote at MarketScan's auto-selected (highest)
        // rate first.
        if (!shouldSetCustomerLocation) {
            setDealStateWithUpdate(updateObj, true)
            return
        }

        // Already chosen for this exact ZIP (e.g. re-picking the same location)?
        // The backend reuses the stored region automatically, so don't re-ask.
        const storedQuote = dealState.formatted_quote
        const alreadySelectedForThisZip = Boolean(
            storedQuote?.tax_region_id && storedQuote?.tax_region_zipcode === location.zipcode
        )

        const regionId = alreadySelectedForThisZip
            ? null
            : await resolveTaxRegion(location, {isPurchase: dealState.is_purchase})

        // One request either way. The chosen region simply rides along with the
        // location it belongs to; the backend validates it against that ZIP and
        // re-prices once. With no selection this is byte-for-byte the update
        // that has always been sent.
        setDealStateWithUpdate(
            regionId == null ? updateObj : {...updateObj, tax_region_id: regionId},
            true,
        )
    }

    const car = dealState.car_detail
    // The deal's selected options — not car.option_codes, which lists every option the trim offers.
    const optionNames = (dealState.option_codes || []).map((option) => option.name).join(', ')
    const isLoading = updateDealResponse?.loading
    const isPurchase = dealState.is_purchase

    // The deal stores customer/dealer location as flat fields; reshape each into
    // the { county, city, state, zipcode } object buildLocationString expects.
    const customerLocation = {
        county: dealState.county,
        city: dealState.city,
        state: dealState.state,
        zipcode: dealState.zipcode,
    }
    const dealerLocation = {
        county: dealState.dealer_county,
        city: dealState.dealer_city,
        state: dealState.dealer_state,
        zipcode: dealState.dealer_zipcode,
    }

    return (
        <Stack
            className={'DealPageInfo_box-container'}
            direction={isTabletOrMobile ? 'column' : 'row'}
            justifyContent="space-between"
            alignItems="center"
        >
            <Box className={'DealPageInfo_box panel-vehicle'}>
                <div className={'panel-header'}>
                    <div className={'panel-title'}>
                        {car.year} {car.make} {car.model}{car.is_new ? '' : ' [Used]'}
                    </div>
                    {
                        isFeatureFlagEnabled('listings') &&
                        <Tooltip title={isHistorical ? '' : 'Find Inventory'} arrow placement={'top'}>
                            <button
                                type="button"
                                className={'panel-header-edit'}
                                disabled={isHistorical}
                                onClick={openListingsModal}
                            >
                                <DirectionsCarFilledOutlinedIcon/>
                            </button>
                        </Tooltip>
                    }
                </div>
                <div className={'panel-body'}>
                    <Tooltip
                        title={`${car.trim} [${car.manufacture_code}]`}
                        arrow
                        placement={'top'}
                        enterDelay={300}
                    >
                        <div className={'vehicle-trim'}>
                            {car.trim} [{car.manufacture_code}]
                        </div>
                    </Tooltip>
                    <div className={'lender-row vin-row'}>
                        <span className={'lender-key'}>Vin:</span>
                        <input
                            className={'plain-input'}
                            type="text"
                            value={(dealState.vin || '').toUpperCase()}
                            placeholder={isHistorical ? '' : 'Add VIN'}
                            readOnly={isHistorical}
                            onChange={(e) => setDealStateWithoutQuoteUpdate({...dealState, vin: e.target.value.toUpperCase()})}
                        />
                        {
                            optionNames &&
                            <Tooltip title={optionNames} arrow placement={'top'} enterDelay={300}>
                                <span className={'vehicle-options'}>{optionNames}</span>
                            </Tooltip>
                        }
                    </div>
                    <div className={'lender-row'}>
                        <span className={'lender-key'}>Demo Miles:</span>
                        <div className={'lender-input-wrap'}>
                            <input
                                type="text"
                                value={dealState.current_mileage || ''}
                                placeholder={''}
                                readOnly={isHistorical}
                                onChange={(e) => setDealStateWithUpdate({current_mileage: e.target.value})}
                            />
                        </div>
                    </div>
                </div>
            </Box>

            <Box className={'DealPageInfo_box panel-location'}>
                <div className={'panel-header'}>
                    <div className={'panel-title'}>Location</div>
                    <Tooltip title={isHistorical ? '' : 'Edit Location'} arrow placement={'top'}>
                        <button
                            type="button"
                            className={'panel-header-edit'}
                            disabled={isHistorical}
                            onClick={() => {
                                openModal({
                                    element: LocationModal,
                                    id: "LocationModal",
                                    props: {
                                        customerLocation,
                                        dealerLocation,
                                        selectLocation: saveDealLocation,
                                        isSaving: updateDealResponse?.loading,
                                    }
                                })
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17.97 9.304A8 8 0 0 0 2 10c0 4.69 4.887 9.562 7.022 11.468"/>
                                <path d="M21.378 16.626a1 1 0 0 0-3.004-3.004l-4.01 4.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z"/>
                                <circle cx="10" cy="10" r="3"/>
                            </svg>
                        </button>
                    </Tooltip>
                </div>
                <div className={'panel-body'}>
                    <div className={'location-row'}>
                        <span className={'location-key'}>Customer:</span>
                        <span className={'location-val'}>
                            { buildLocationString(customerLocation) }
                        </span>
                    </div>
                    <div className={'location-row'}>
                        <span className={'location-key'}>Dealer:</span>
                        <span className={'location-val'}>
                            { buildLocationString(dealerLocation) }
                        </span>
                    </div>
                </div>
            </Box>
        </Stack>
    )
}